import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './lib/firebase';
import { MotionConfig } from 'framer-motion';

// Components
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import Modal from './components/Modal';
import Spinner from './components/Spinner';
import RecommendedRequirements from './components/RecommendedRequirements'; 

// Pages
import LoginPage from './pages/LoginPage';
import StudentDashboard from './features/student/StudentDashboard';
import AdminDashboard from './features/admin/AdminDashboard';
import SuperAdminDashboard from './features/admin/SuperAdminDashboard';
import ExamTaker from './features/exam/ExamTaker';
import ExamResult from './features/exam/ExamResult'; 
import LectureRoom from './features/lecture/LectureRoom';
import HomeworkRoom from './features/homework/HomeworkRoom';

// NEW: Instil IQ Practice Features
import InstilIQHub from './features/practice/InstilIQHub';
import InstilIQTaker from './features/practice/InstilIQTaker';
import TrackerPage from './features/tracker/TrackerPage';
import CoordinatorInbox from './features/attendance/CoordinatorInbox';
import ExamAccessProvider from './features/attendance/ExamAccessProvider';
import StudentGuideProvider from './features/onboarding/StudentGuideProvider';
import StudentGuidePage from './features/onboarding/StudentGuidePage';

// --- Dashboard Wrapper ---
const RoleBasedDashboard = ({ setModal }) => {
    const { user, userData } = useAuth();

    if (!userData) return <div className="flex justify-center mt-10"><Spinner /></div>;

    switch (userData.role) {
        case 'superadmin':
            return <SuperAdminDashboard user={user} setModal={setModal} />;
        case 'admin':
            return <AdminDashboard user={user} setModal={setModal} />;
        case 'student':
            return <StudentDashboard user={user} userData={userData} setModal={setModal} />;
        default:
            return <div className="text-center mt-10">Unknown Role</div>;
    }
};

// --- Wrappers for Components requiring specific props via Hooks ---

// Wrapper to fetch data for ExamResult page
const ResultPageWrapper = ({ setModal }) => {
    const { examId, attemptId } = useParams();
    const { user, userData } = useAuth();
    const [exam, setExam] = useState(null);
    const [result, setResult] = useState(null);
    const [paper, setPaper] = useState(null); 
    const [loading, setLoading] = useState(true);

    React.useEffect(() => {
        let active = true;
        setLoading(true); setExam(null); setResult(null); setPaper(null);
        const loadData = async () => {
            try {
                const examSnap = await getDoc(doc(db, 'exams', examId));
                const attemptSnap = await getDoc(doc(db, 'attempts', attemptId));
                
                if (!active) return;
                if (examSnap.exists() && attemptSnap.exists()) {
                    const attempt = attemptSnap.data();
                    if (attempt.examId !== examId || (userData?.role === "student" && attempt.userId !== user.uid)) return;
                    const examData = { id: examSnap.id, ...examSnap.data() };
                    setExam(examData);
                    setResult({ id: attemptSnap.id, ...attemptSnap.data() });

                    if (examData.paperId) {
                        const paperSnap = await getDoc(doc(db, 'papers', examData.paperId));
                        if (active && paperSnap.exists()) {
                            setPaper({ id: paperSnap.id, ...paperSnap.data() });
                        }
                    }
                }
            } catch (e) {
                console.error("Error loading result context:", e);
            } finally {
                if (active) setLoading(false);
            }
        };
        loadData();
        return () => { active = false; };
    }, [examId, attemptId, user.uid, userData?.role]);

    if (loading) return <div className="flex justify-center mt-10"><Spinner /></div>;
    
    if (!exam || !result || !paper) return <div className="text-center mt-10">Result or Syllabus not found.</div>;

    // --- RBAC Direct URL Guard ---
    if (userData?.role === 'admin' && userData?.allowedFeatures) {
        // Must have either viewing or grading permissions
        const hasFeature = userData.allowedFeatures.includes('view_reports') || userData.allowedFeatures.includes('evaluate_answers');
        // Must be assigned to the specific paper this exam belongs to
        const hasPaper = !exam.paperId || userData.assignedPapers?.includes(exam.paperId);
        
        if (!hasFeature || !hasPaper) {
            return <div className="text-center mt-10 p-6 text-red-600 font-bold bg-red-50 border border-red-200 rounded-lg max-w-2xl mx-auto">
                Access Denied: You do not have permission to view results for this specific ACCA paper.
            </div>;
        }
    }

    return <ExamResult exam={exam} result={result} paper={paper} onBackToList={() => window.history.back()} setModal={setModal} />;
};

const LectureRoomWrapper = () => {
    const { lectureId } = useParams();
    const { user, userData } = useAuth(); 
    const [lecture, setLecture] = useState(null);
    const [loadError, setLoadError] = useState(null);
    
    React.useEffect(() => {
        let active = true;
        setLecture(null); setLoadError(null);
        const load = async () => {
            try {
                const snap = await getDoc(doc(db, 'lectures', lectureId));
                if (!active) return;
                if (snap.exists()) setLecture({ ...snap.data(), id: snap.id });
                else setLoadError('This lecture is no longer available.');
            } catch { if (active) setLoadError('Unable to load this lecture. Check your connection and try again.'); }
        };
        load();
        return () => { active = false; };
    }, [lectureId]);
    if (loadError) return <div role="alert" className="p-8 text-center"><p>{loadError}</p><a className="underline" href="/dashboard">Back to dashboard</a></div>;


    if (!lecture || !userData) return <div className="flex justify-center mt-10"><Spinner /></div>;

    // --- RBAC Direct URL Guard ---
    if (userData?.role === 'admin' && userData?.allowedFeatures) {
        const hasFeature = userData.allowedFeatures.includes('manage_exams');
        const hasPaper = !lecture.paperId || userData.assignedPapers?.includes(lecture.paperId);
        
        if (!hasFeature || !hasPaper) {
            return <div className="text-center mt-10 p-6 text-red-600 font-bold bg-red-50 border border-red-200 rounded-lg max-w-2xl mx-auto">
                Access Denied: You do not have permission to enter lectures for this specific ACCA paper.
            </div>;
        }
    }

    const isAdmin = userData.role === 'admin' || userData.role === 'superadmin';
    const mode = isAdmin ? 'admin-live' : 'student';

    return <LectureRoom lecture={lecture} user={user} userRole={userData.role} onBack={() => window.history.back()} mode={mode} />;
};

const HomeworkRoomWrapper = () => {
    const { homeworkId } = useParams();
    const { user, userData } = useAuth(); // Added userData here for RBAC
    const [homework, setHomework] = useState(null);
    const [loadError, setLoadError] = useState(null);

    React.useEffect(() => {
        let active = true;
        setHomework(null); setLoadError(null);
        const load = async () => {
            try {
                const snap = await getDoc(doc(db, 'homeworks', homeworkId));
                if (!active) return;
                if (snap.exists()) setHomework({ ...snap.data(), id: snap.id });
                else setLoadError('This homework is no longer available.');
            } catch { if (active) setLoadError('Unable to load this homework. Check your connection and try again.'); }
        };
        load();
        return () => { active = false; };
    }, [homeworkId]);
    if (loadError) return <div role="alert" className="p-8 text-center"><p>{loadError}</p><a className="underline" href="/dashboard">Back to dashboard</a></div>;


    if (!homework || !userData) return <div className="flex justify-center mt-10"><Spinner /></div>;

    // --- RBAC Direct URL Guard ---
    if (userData?.role === 'admin' && userData?.allowedFeatures) {
        const hasFeature = userData.allowedFeatures.includes('manage_exams');
        const hasPaper = !homework.paperId || userData.assignedPapers?.includes(homework.paperId);
        
        if (!hasFeature || !hasPaper) {
            return <div className="text-center mt-10 p-6 text-red-600 font-bold bg-red-50 border border-red-200 rounded-lg max-w-2xl mx-auto">
                Access Denied: You do not have permission to access homework for this specific ACCA paper.
            </div>;
        }
    }

    return <HomeworkRoom homework={homework} user={user} onBack={() => window.history.back()} />;
};

// --- Main App ---
export default function App() {
    // Global Modal State
    const [modal, setModal] = useState({ show: false, message: '' });
    const closeModal = () => setModal({ show: false, message: '' });

    return (
        <MotionConfig reducedMotion="user"><AuthProvider>
            <BrowserRouter><ExamAccessProvider><StudentGuideProvider>
                {/* Global Modal Layer */}
                {modal.show && <Modal message={modal.message} onClose={closeModal} />}
                <CoordinatorInbox/>
                
                <Routes>
                    {/* Public Route */}
                    <Route path="/login" element={<LoginPage />} />

                    {/* Protected Routes (Require Login) */}
                    <Route element={<ProtectedRoute />}>
                        
                        {/* GROUP 1: Standard Views (With Sidebar/Header) */}
                        <Route element={<Layout setModal={setModal} />}>
                            <Route path="/" element={<Navigate to="/dashboard" replace />} />
                            <Route path="/dashboard" element={<RoleBasedDashboard setModal={setModal} />} />
                            <Route path="/exam/:examId/result/:attemptId" element={<ResultPageWrapper setModal={setModal} />} />
                            
                            {/* System Requirements Route */}
                            <Route path="/student-guide" element={<StudentGuidePage />} />
                            <Route path="/system-requirements" element={<RecommendedRequirements />} />

                            {/* Instil IQ Practice Selection Dashboard (Per Paper) */}
                            <Route path="/iq-hub/:paperId" element={<InstilIQHub />} />
                            <Route path="/tracker" element={<TrackerPage />} />
                            <Route path="/tracker/:paperId" element={<TrackerPage />} />
                        </Route>

                        {/* GROUP 2: Full Screen Views (NO Sidebar/Header) */}
                        <Route path="/exam/:examId" element={<ExamTaker setModal={setModal} />} />
                        <Route path="/lecture/:lectureId" element={<LectureRoomWrapper />} />
                        <Route path="/homework/:homeworkId" element={<HomeworkRoomWrapper />} />

                        {/* Instil IQ Interactive Practice Platform View (Split Screen) */}
                        <Route path="/iq-practice/:paperId/topic/:topicId" element={<InstilIQTaker />} />
                        <Route path="/iq-practice/:paperId/day/:date" element={<InstilIQTaker />} />

                    </Route>

                    {/* Catch all */}
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
            </StudentGuideProvider></ExamAccessProvider></BrowserRouter>
        </AuthProvider></MotionConfig>
    );
}